/*
  Volume ladder — the script behind snippets/kinetic-volume-ladder.liquid.

  It used to be inlined in that snippet. It lives here because the ladder now
  also renders inside the quick view drawer, which the theme injects with
  innerHTML — and innerHTML never executes <script> tags. One copy, loaded
  once from layout/theme.liquid, drives every ladder on the page: the PDP's
  own on load, and any quick view's the moment its markup lands (watched via
  a MutationObserver on <body>).

  Every instance is SCOPED to its own container — the quick view drawer
  ([data-product-quickview-ajax]) or the PDP buy box ([data-kvd-product]) —
  so a PDP with a quick view open never rewrites the other's prices. All
  price strings come pre-formatted from Liquid (data-kvd-prices /
  data-kvd-subs); nothing here does currency maths.
*/
(function () {
  'use strict';

  function init(root) {
    if (root.hasAttribute('data-kvd-ready')) return;
    root.setAttribute('data-kvd-ready', '');

    var scope =
      root.closest('[data-product-quickview-ajax]') ||
      root.closest('[data-kvd-product]') ||
      document;

    var prices;
    try {
      prices = JSON.parse(root.getAttribute('data-kvd-prices'));
    } catch (error) {
      return; // A broken map must leave the server-rendered state alone.
    }

    var subs = {};
    try {
      subs = JSON.parse(root.getAttribute('data-kvd-subs')) || {};
    } catch (error) {
      subs = {}; // No subscription map just means the card is left alone.
    }

    var BEST = root.getAttribute('data-kvd-best') || 'Best price unlocked';
    var ADD_ONE = root.getAttribute('data-kvd-add-one') || 'Add 1 more for';
    var ADD_MANY = root.getAttribute('data-kvd-add-many') || 'Add %n% more for';
    var OFF = root.getAttribute('data-kvd-off') || 'off';

    // The legend no longer echoes the selected count (the dark tile says it);
    // kept null-safe in case a cached older snippet still renders the span.
    var unitsLabel = root.querySelector('[data-kvd-units-label]');
    var tierPct = root.querySelector('[data-kvd-tier-pct]');
    var nudge = root.querySelector('[data-kvd-nudge]');
    var options = root.querySelectorAll('[data-kvd-unit-option]');

    // The theme's own price surfaces, rendered by product-price-custom.liquid
    // and subscription-widget.liquid. assets/subscription-widget.js rewrites
    // all of them on every purchase-option click and has no notion of
    // quantity, so the ladder re-applies AFTER it rather than trying to own
    // them outright. Resolved per call because that script replaces text
    // nodes, not elements. The quick view has no subscription widget, so
    // there only main / perShot / atc resolve.
    function surfaces() {
      return {
        main: scope.querySelector('.product__block__price .product__price--main'),
        compare: scope.querySelector('.product__block__price .compare__price'),
        perShot: scope.querySelector('.product__price--off-amount'),
        oneTime: scope.querySelector('.widget__option-price[data-type="onetime"]'),
        atc: scope.querySelectorAll('.atc-button-price'),
        planRows: scope.querySelectorAll('.selling__plan')
      };
    }

    // Bold's price for one box on a given plan, already multiplied out to
    // the boxes selected. Returns { paid, perShot } or null.
    function subRowFor(planId, units) {
      var plan = planId == null ? null : subs[String(planId)];
      return plan ? plan[String(units)] || null : null;
    }

    // No row carries `active` until the customer opens the card, so fall
    // back to the first plan — the same one the form would submit.
    function activePlanId() {
      var active =
        scope.querySelector('.selling__plan.active[data-plan-id]') ||
        scope.querySelector('.selling__plan[data-plan-id]');
      return active ? active.getAttribute('data-plan-id') : null;
    }

    function currentUnits() {
      var checked = root.querySelector('[data-kvd-unit-option]:checked');
      return checked ? parseInt(checked.getAttribute('data-kvd-units'), 10) : 1;
    }

    // In subscribe mode the volume rung still applies (deck rule 3), so the
    // tier badge and the nudge read the same in both modes (merchant call,
    // 2026-08-26 — each figure is true on its own; the compounded total is
    // carried by the per-delivery price, never stated as a percentage).
    // Only the price figures switch to the compounded per-delivery map.
    function isSubscription() {
      var active = scope.querySelector('.subscription-widget__option.active');
      return !!active && active.getAttribute('data-type') === 'subscription';
    }

    function firstOrderOn() {
      return document.documentElement.hasAttribute('data-kfo-unlocked');
    }

    function escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    // Every write is a no-op when the node already says the right thing.
    // That is what makes the observer below safe: a re-assert that changes
    // nothing emits no mutation, so it cannot feed itself.
    function write(node, text) {
      if (!node || node.textContent.trim() === text) return;
      node.textContent = text;
    }

    // Brief §3: no single-box subscription. Below the minimum the Subscribe
    // card is removed from the flow, and if it was the active choice the
    // one-time card is clicked so subscription-widget.js runs its own
    // handler — that is what disables the form's `selling_plan` input and
    // reprices, so a 1-box add can never carry a plan. The click re-enters
    // apply() through the scope listener; by then the card is inactive,
    // so it settles in one pass.
    var SUB_MIN = parseInt(root.getAttribute('data-kvd-sub-min'), 10) || 1;

    function syncSubscribe(units) {
      var recurring = scope.querySelector('.subscription-widget__option_recurring');
      if (!recurring) return;
      var allowed = units >= SUB_MIN;
      if (recurring.hidden === allowed) recurring.hidden = !allowed;
      if (!allowed && recurring.classList.contains('active')) {
        var onetime = scope.querySelector('.subscription-widget__option_onetime');
        if (onetime) onetime.click();
      }
    }

    function apply() {
      var units = currentUnits();
      var row = prices[String(units)];
      if (!row) return;

      syncSubscribe(units);
      var subscribing = isSubscription();
      var el = surfaces();

      write(unitsLabel, String(units));
      write(tierPct, row.pct ? '−' + row.pct + '%' : '');

      if (nudge) {
        var markup;
        if (row.nextPct) {
          var lead = row.unitsAway === 1 ? ADD_ONE : ADD_MANY.replace('%n%', row.unitsAway);
          markup =
            escapeHtml(lead) + ' <strong>' + row.nextPct + '% ' + escapeHtml(OFF) + '</strong>';
        } else {
          markup = '<strong>' + escapeHtml(BEST) + '</strong>';
        }
        if (nudge.innerHTML !== markup) nudge.innerHTML = markup;
      }

      // First-order 40% unlocked on this page (assets/kinetic-first-order.js):
      // the code covers one-time purchases only and stands alone, so the
      // one-time figures switch to its price with LIST struck beside it.
      // Subscription figures are untouched. `unlocked` is null where the code
      // would not beat the ladder.
      var firstOrder = firstOrderOn() && row.unlocked ? row : null;
      var oneTimePaid = firstOrder ? row.unlocked : row.paid;

      // The one-time card states the one-time price in either mode — that is
      // the ladder's own number, and it is what makes switching informative.
      // Rebuilt rather than patched because subscription-widget.js sets
      // textContent on this node, which would drop the compare span.
      if (el.oneTime) {
        var wantCompare = firstOrder || row.pct ? row.list : '';
        var compare = el.oneTime.querySelector('.option__compare');
        if (!compare) {
          el.oneTime.textContent = '';
          el.oneTime.appendChild(document.createTextNode(oneTimePaid));
          compare = document.createElement('span');
          compare.className = 'option__compare';
          el.oneTime.appendChild(compare);
        } else if (el.oneTime.firstChild.nodeValue.trim() !== oneTimePaid) {
          el.oneTime.firstChild.nodeValue = oneTimePaid;
        }
        write(compare, wantCompare);
        el.oneTime.classList.toggle('kvd-first-order', !!firstOrder);
      }
      if (el.main) el.main.classList.toggle('kvd-first-order', !!firstOrder && !subscribing);

      // Every plan row states the cost of THIS delivery — Bold's per-box
      // price times the boxes selected. The widget's own script rewrites
      // these from a single box, which is why 4 boxes still read €25,42.
      // Beside it, struck, the ONE-TIME price for the same boxes: that gap
      // is the subscription discount made visible as money, on top of the
      // volume rung the legend already states. Same language as the
      // one-time card striking the list price — each layer strikes the
      // layer before it, and no percentage is ever summed.
      Array.prototype.forEach.call(el.planRows, function (rowEl) {
        var priceEl = rowEl.querySelector('.selling_plan-price span');
        var planRow = subRowFor(rowEl.getAttribute('data-plan-id'), units);
        if (!priceEl || !planRow) return;
        write(priceEl, planRow.paid);
        var compare = rowEl.querySelector('.kvd-plan-compare');
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'kvd-plan-compare';
          priceEl.insertAdjacentElement('afterend', compare);
        }
        write(compare, row.paid);
      });

      if (subscribing) {
        var active = subRowFor(activePlanId(), units);
        if (active) {
          write(el.main, active.paid);
          write(el.perShot, active.perShot);
          Array.prototype.forEach.call(el.atc, function (node) {
            write(node, active.paid);
          });
        }
        return;
      }

      // The row above the buy button carries the price actually charged and
      // the per-shot rate, nothing else. The saving is already stated twice
      // above it — as the tier badge and as the struck list price on the
      // one-time card — so a third reading here would only add noise.
      write(el.main, oneTimePaid);
      if (el.compare) {
        write(el.compare, '');
        el.compare.classList.add('hide');
      }
      write(el.perShot, firstOrder ? row.unlockedPerShot : row.perShot);
      Array.prototype.forEach.call(el.atc, function (node) {
        write(node, oneTimePaid);
      });
    }

    // This product carries a free gift, and that widget deliberately opens
    // with NOTHING selected, covering the buy button with a grey "select
    // your purchase plan" overlay. A ladder page states a real one-time
    // price from the first paint, so it opens on One-time instead. Only ever
    // while nothing is selected, so a deliberate switch to Subscribe stands.
    function primeOneTime() {
      if (scope.querySelector('.subscription-widget__option.active')) return;
      var onetime = scope.querySelector('.subscription-widget__option_onetime');
      if (!onetime) return;
      onetime.classList.add('active');
      var overlay = scope.querySelector('.select-plan');
      if (overlay) overlay.classList.add('hidden');
    }

    // Run on the next tick so it lands after subscription-widget.js's own
    // handler for the same click, and after the theme's quantity handler.
    function reapply() {
      setTimeout(function () {
        observe();
        primeOneTime();
        apply();
      }, 0);
    }

    Array.prototype.forEach.call(options, function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        apply();   // immediate, so the tap feels instant
        reapply(); // and again after any theme handler for the same event
      });
    });

    // On the scope, not the document: a quick view's listener dies with the
    // drawer's markup instead of piling up on every open.
    var clickTarget = scope === document ? document : scope;
    clickTarget.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      if (event.target.closest('.subscription-widget__option, .selling__plan')) reapply();
    });

    // Several theme scripts rewrite these same nodes from their own idea of
    // the selected variant, none of which knows about quantity — and they do
    // it on their own schedule, not only in response to a click we can see.
    // So rather than race them, watch the nodes and re-assert. Safe against
    // feedback because every write above is a no-op when the value already
    // matches, and a write that changes nothing emits no mutation.
    var observer = null;
    var watched = [];

    function observe() {
      if (!window.MutationObserver) return;
      if (!observer) {
        var settling = false;
        observer = new MutationObserver(function () {
          if (settling) return;
          settling = true;
          requestAnimationFrame(function () {
            settling = false;
            apply();
          });
        });
      }
      // On the PDP the ladder renders above both of these in the block loop,
      // so neither exists while the inline markup parses — attach once they do.
      ['.product__block__price', '.subscription-widget__container'].forEach(function (sel) {
        var node = scope.querySelector(sel);
        if (!node || watched.indexOf(node) !== -1) return;
        watched.push(node);
        observer.observe(node, { childList: true, subtree: true, characterData: true });
      });
    }

    // The first-order card unlocks (or restores a code) after this ladder
    // initialised, so re-price when it says so.
    document.addEventListener('kinetic:first-order:unlocked', reapply);

    primeOneTime();
    apply();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reapply);
    } else {
      reapply();
    }
    window.addEventListener('load', reapply);
  }

  // Initialise every ladder already in the document, then keep watching for
  // ones that arrive later — the quick view drawer injects its product form
  // with innerHTML after a fetch, well after load.
  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.matches('[data-kvd-ladder]')) init(node);
    Array.prototype.forEach.call(node.querySelectorAll('[data-kvd-ladder]'), init);
  }

  function boot() {
    scan(document.documentElement);
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        Array.prototype.forEach.call(records[i].addedNodes, scan);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Cart stepper floor for subscription lines (brief §3: a single box is
  // never sold on subscription). The min attribute already stops the minus
  // button (theme.js reads it), but the input is free-typed and the theme's
  // cart update handler listens directly on it — so clamp in the capture
  // phase, which runs first, and the value it reads is the clamped one.
  // Capture also catches the stepper's own change events, which don't bubble.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.getAttribute || input.getAttribute('data-kvd-qty-min') === null) return;
    var min = parseInt(input.getAttribute('data-kvd-qty-min'), 10);
    var val = parseInt(input.value, 10);
    if (isNaN(min)) return;
    if (isNaN(val) || val < min) input.value = min;
  }, true);
})();
